import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ChevronDown, Globe } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';

export function LanguageSelector() {
  const { language, setLanguage, languages } = useLanguage();
  const currentLang = languages.find(l => l.code === language);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="flex items-center gap-2 text-[#A1A1AA] hover:text-white"
          data-testid="language-selector"
        >
          <Globe className="w-4 h-4" />
          <span className="hidden sm:inline">{currentLang?.name}</span>
          <span className="sm:hidden">{currentLang?.flag}</span>
          <ChevronDown className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-[#141417] border-[#27272A]">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            className={`flex items-center gap-2 cursor-pointer ${language === lang.code ? 'text-[#007AFF]' : 'text-[#A1A1AA]'} hover:text-white hover:bg-[#1D1D21]`}
            data-testid={`lang-${lang.code}`}
          >
            <span>{lang.flag}</span>
            <span>{lang.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function BackToHomeButton({ variant = 'primary', className = '' }) {
  const { t } = useLanguage();
  
  return (
    <Link to="/">
      <Button 
        className={variant === 'primary' ? `btn-primary ${className}` : `btn-secondary ${className}`}
        data-testid="back-to-home-btn"
      >
        {t('nav.backToHome')}
      </Button>
    </Link>
  );
}

export function StickyHeader({ showBackHome = false, children }) {
  return (
    <nav className="glass-header fixed top-0 left-0 right-0 z-50 border-b border-[#27272A]">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {children}
      </div>
    </nav>
  );
}

export function Footer() {
  const { t } = useLanguage();
  
  return (
    <footer className="bg-[#141417] border-t border-[#27272A] py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Back to Home - Prominent */}
        <div className="flex justify-center mb-8">
          <BackToHomeButton className="text-lg px-8 py-4" />
        </div>
        
        {/* Disclaimer */}
        <div className="bg-[#09090B] border border-[#27272A] rounded-md p-6 mb-8">
          <h3 className="text-white font-bold mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {t('legal.disclaimer')}
          </h3>
          <p className="text-[#71717A] text-sm leading-relaxed">
            The information provided by Cossy Network Group LLC is for educational and informational purposes only and should not be considered financial, investment, tax, or legal advice. Trading and investing in financial markets such as stocks, options, futures, forex, and cryptocurrencies involves significant risk. You may lose part or all of your invested capital. Past performance does not guarantee future results. Any trading decisions you make are solely your responsibility. Always conduct your own research and consider consulting with a licensed financial advisor before making any investment decisions.
          </p>
        </div>
        
        {/* Footer Links */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#007AFF] rounded-sm flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <span className="font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>COSSY NETWORK</span>
          </div>
          
          <div className="flex items-center gap-6 text-[#71717A] text-sm">
            <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">
              {t('legal.terms')}
            </Link>
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">
              {t('legal.privacy')}
            </Link>
            <a href="mailto:support@cossynetworkgroup.com" className="hover:text-white transition-colors">
              {t('legal.contact')}
            </a>
          </div>
          
          <p className="text-[#71717A] text-sm">© 2024 Cossy Network Group LLC. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
